import React, { useCallback, useContext, useMemo, useState } from 'react';
import { EDInputGroup } from '../../../../../../../BaseComponents/InputGroup/EDInputGroup';
import EditableAttribute from '../../../../../../../Components/Attributes/EditableAttribute/EditableAttribute';
import { getAttributeSafe } from '../../../../../../../Components/Urval/Utils/urvalUtils';
import clientAvenyModel from '../../../../../../../Model/clientAvenyModel';
import { ModelContext } from '../../../../../../../Model/ModelContext/ModelContext';
import {
  IClientAttributeModel,
  IClientBusinessModel,
} from '../../../../../../../Types/ClientModelTypes';
import {
  IBusinessCollectionProps,
  IObjektVy,
  IObjektVyComp,
} from '../../../../../../../Types/ObjektVy';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { BokforderType__Associations_Bokforingsmall__Associations_Parametrar_All } from '../../Queries/_QueryTypes/BokforderType';
import {
  TEDEKBokforderRadLIKVIDSATTEnum,
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderTYPEnum,
  TEDEKBokforderVariantEnum,
  TEDEKBokforingsmallParamFORESLA_BOKFDATUMEnum,
  TEDEKBokforingsmallParamPARAM_DATATYPEnum,
  TEDEKBokforingsmallRadDEB_KREDEnum,
  TEDSelectionOperator,
} from '../../../../../../../globalQueryTypes';
import BokforderContext from '../BokforderContext/BokforderContext';
import {
  createLabelText,
  createParamString,
  isRowEmpty,
  paramIsDateType,
  sortByOrdningsNr,
} from '../../Utils/BokforderUtils';
import { DEBOUNCE_DELAY_TIME } from '../../../../../../../Const/SystemWideConsts';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDDivider from '../../../../../../../BaseComponents/Divider/EDDivider';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { capitalize } from '../../../../../../../Utils/Formatters/Utils/StringUtils';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import mergeSort from '../../../../../../../Utils/mergeSort';
import { equals, isEmpty } from 'ramda';
import { incDay } from '../../../../../../../Utils/dateUtils';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import {
  DefaultBfoVariantEnum,
  IUserBokforderKodbehorigheter,
} from '../BokforderContext/BokforderContextManager';
import { useIsBreakpointOrLarger } from '../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import AttestantPicker from '../AttestantPicker/AttestantPicker';
import Attribut from '../../../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import { parseParamsValues } from '../../Utils/BokforderKonteringUtils';
import { roundToTwoDecimals } from '../../../../../Leverantor/ObjektVyer/FakturaDel/Components/Kontering/Utils/KonteringUtils';

interface IProps extends IBusinessCollectionProps {}

const BokforderKonteringComp: IObjektVyComp = {
  caption: '',
  comp: (props: IProps) => <BokforderKonteringView {...props} />,
};

const bokforderModel: IClientBusinessModel =
  clientAvenyModel.EK.BusinessModels.EKBokforder;

const BokforderKonteringView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const bokforderContext = useContext(BokforderContext);
  const formContext = useContext(BusinessDataFormContext);
  const formContextUpdate = formContext?.update;
  const konteringContext = useContext(KonteringContext);
  const getKontodelDefsByKodplanId =
    konteringContext.konteringUtils.getKontodelDefsByKodplanId;
  const modelContext = useContext(ModelContext);
  const isMediumOrLarger = useIsBreakpointOrLarger('md');
  const [valdAttestant, setValdAttestant] =
    useState<null | IUserBokforderKodbehorigheter>(null);

  const attestanter: string = formContext?.data?.AttestantFullnames;
  const [prevAttestanter, setPrevAttestanter] = useState<null | string>(
    attestanter
  );

  const templateDateAttribute = useMemo(
    () =>
      getAttributeSafe(
        'EKBokforder',
        'MALL_DATUM',
        modelContext.avenyCollectionMap
      ),
    [modelContext.avenyCollectionMap]
  );
  const templateIdentificationAttribute = useMemo(
    () =>
      getAttributeSafe(
        'EKBokforder',
        'MALL_IDENTIFIKATION',
        modelContext.avenyCollectionMap
      ),
    [modelContext.avenyCollectionMap]
  );
  const templateLiquidAttribute = useMemo(
    () =>
      getAttributeSafe(
        'EKBokforder',
        'MALL_LIKVIDSATT',
        modelContext.avenyCollectionMap
      ),
    [modelContext.avenyCollectionMap]
  );
  const templatePlaceAttribute = useMemo(
    () =>
      getAttributeSafe(
        'EKBokforder',
        'MALL_PLATS',
        modelContext.avenyCollectionMap
      ),
    [modelContext.avenyCollectionMap]
  );
  const templatePurposeAttribute = useMemo(
    () =>
      getAttributeSafe(
        'EKBokforder',
        'MALL_ANDAMAL',
        modelContext.avenyCollectionMap
      ),
    [modelContext.avenyCollectionMap]
  );

  const templateAttributes = useMemo(
    () => ({
      MALL_DATUM: templateDateAttribute,
      MALL_IDENTIFIKATION: templateIdentificationAttribute,
      MALL_LIKVIDSATT: templateLiquidAttribute,
      MALL_PLATS: templatePlaceAttribute,
      MALL_ANDAMAL: templatePurposeAttribute,
    }),
    [
      templateDateAttribute,
      templateIdentificationAttribute,
      templateLiquidAttribute,
      templatePlaceAttribute,
      templatePurposeAttribute,
    ]
  );

  const kontodelDefFromKodplan = useMemo(() => {
    return getKontodelDefsByKodplanId?.(
      bokforderContext.bokforderKodplan?.id ?? ''
    );
  }, [bokforderContext.bokforderKodplan?.id, getKontodelDefsByKodplanId]);

  const params = mergeSort(
    bokforderContext.currentTemplate?._Associations?.Parametrar_All ?? [],
    sortByOrdningsNr
  ) as BokforderType__Associations_Bokforingsmall__Associations_Parametrar_All[];

  const text = bokforderContext.currentTemplate?.TEXT ?? '';

  const onChange = useCallback(
    (
        param: BokforderType__Associations_Bokforingsmall__Associations_Parametrar_All,
        isCustomField: boolean,
        ignoreFormHasChanged: boolean
      ) =>
      (value: string) => {
        let updateArray: FormUpdateAssociatonType[][] = [];
        const newValue = {
          [param.PARAM_NAME]: value,
        };

        const newParamValues = {
          ...bokforderContext.paramValues,
          ...newValue,
        };

        if (
          param.PARAM_NAME === 'MALL_DATUM' &&
          value !== '' &&
          param.FORESLA_BOKFDATUM != null &&
          param.FORESLA_BOKFDATUM !=
            TEDEKBokforingsmallParamFORESLA_BOKFDATUMEnum.FORESLA_INTE &&
          (isEmpty(formContext?.formState.currentValues?.BOKF_DATUM) ||
            formContext?.formState.currentValues?.BOKF_DATUM == undefined ||
            formContext?.formState.currentValues?.BOKF_DATUM === 'Invalid date')
        ) {
          let addDays = 1;
          let bokfdateFromTemplate = new Date(value);

          switch (param.FORESLA_BOKFDATUM) {
            case TEDEKBokforingsmallParamFORESLA_BOKFDATUMEnum.NASTA_DAG:
              bokfdateFromTemplate = incDay(bokfdateFromTemplate, addDays);
              break;
            case TEDEKBokforingsmallParamFORESLA_BOKFDATUMEnum.NASTKOMMANDE_VARDAG:
              if (bokfdateFromTemplate.getDay() === 6) {
                addDays++;
              } else if (bokfdateFromTemplate.getDay() === 5) {
                addDays += 2;
              }
              bokfdateFromTemplate = incDay(bokfdateFromTemplate, addDays);
              break;

            default:
              break;
          }

          updateArray.push([
            {
              operation: 'OwnFields',
              values: {
                BOKF_DATUM: bokfdateFromTemplate.toLocaleDateString('sv-SE'),
              },
            },
          ]);
        }

        if (!isCustomField) {
          updateArray.push([
            {
              operation: 'OwnFields',
              values: { ...newValue },
            },
          ]);
        }
        updateArray.push([
          {
            operation: 'OwnFields',
            values: {
              PARAMVALUES: createParamString(newParamValues),
              TEXT: createLabelText(text, newParamValues),
            },
          },
        ]);

        formContextUpdate?.(updateArray, ignoreFormHasChanged);
      },
    [
      bokforderContext.paramValues,
      formContextUpdate,
      formContext?.formState.currentValues?.BOKF_DATUM,
      text,
    ]
  );

  const isAttestantPickerDisabled =
    formContext?.formState.loading ||
    konteringContext.konteringValidationQueryLoading;

  const onBeloppChange = (value: string) => {
    const newValue = {
      BOKF_BELOPP: value,
    };

    const newParamValues = {
      ...bokforderContext.paramValues,
      ...newValue,
    };

    let autoFilledDebetKreditValues: FormUpdateAssociatonType[][] =
      bokforderContext?.templateRows?.reduce<FormUpdateAssociatonType[][]>((prev, current) => {
        // Hoppa över templateRows som inte syns i griden eller har en andel i procent på radmallen.
        if (current.andelProcent) {
          const newFormUpdateValue: FormUpdateAssociatonType = {
            associationName: 'Rader',
            operation: current.id.includes(NEWLY_ADDED_ID_PREFIX_FOR_UPDATE)
              ? 'Add_Values'
              : 'Update_Values',
            id: current.id,
            values: {
              DEBET:
                current.debetOrKredit ===
                  TEDEKBokforingsmallRadDEB_KREDEnum.DEBET &&
                value != null &&
                value != ''
                  ? roundToTwoDecimals(current.andelProcent * parseFloat(value))
                  : null,
              KREDIT:
                current.debetOrKredit ===
                  TEDEKBokforingsmallRadDEB_KREDEnum.KREDIT &&
                value != null &&
                value != ''
                  ? roundToTwoDecimals(current.andelProcent * parseFloat(value))
                  : null,
            },
          };
          return [...prev, [newFormUpdateValue]];
        }
        return prev;
      }, []);

    formContext?.update([
      [
        {
          operation: 'OwnFields',
          values: {
            PARAMVALUES: createParamString(newParamValues),
          },
        },
      ],
      ...autoFilledDebetKreditValues,
    ]);
  };

  const changesAreMadeInGrid = useMemo(() => {
    const result = equals(
      formContext?.formState.autoValues._Associations?.Rader_Add_Values,
      formContext?.formState.values?._Associations?.Rader_Add_Values
    );

    return !result;
  }, [
    formContext?.formState.autoValues._Associations?.Rader_Add_Values,
    formContext?.formState.values._Associations?.Rader_Add_Values,
  ]);

  const beloppAttribute: IClientAttributeModel = {
    AttributeName: 'BOKF_BELOPP', // AttributeName
    DisplayLabel: 'Belopp',
    Hint: 'Belopp',
    DataType: 'Currency',
    EnumValues: new Map(),
    Attribute: 'Normal', // Combination of Attribute and Exportable: Normal = Attribute and Exportable, Internal = Attribute and not Exportable, None = not Attribute
    Condition: 'Normal', // Combination of Condition and InternalCondition: Normal = Condition and not InternalCondition, Internal = Condition and InternalCondition, None = not Condition
    Formatter: '', // Formatter
    FormatterParseParams: '', // FormatterParseParams
    FormatterDisplayParams: '', // FormatterParseParams
    FormatterValidateParams: '', // FormatterValidateParams
    GUIEditable: true, // Can this field be modified by the end user?
    InterfaceType: 'ifNumeric', // InterfaceType (Normal Condition only)
    CondOperators: [
      TEDSelectionOperator.equals,
      TEDSelectionOperator.notequals,
      TEDSelectionOperator.interval,
      TEDSelectionOperator.notinterval,
      TEDSelectionOperator.notless,
      TEDSelectionOperator.greater,
      TEDSelectionOperator.notgreater,
      TEDSelectionOperator.less,
      TEDSelectionOperator.hasvalue,
      TEDSelectionOperator.isnull,
    ], // CondOperators (Normal Condition only)
    DefaultCondOperator: TEDSelectionOperator.interval,
    ValidInComps: [], // Valid In Classes
    AttributeGroup: '', // AttributeGroup
    AttributeParams: {},
    Properties: new Map(),
  };

  const hideTemplateInputFields = useMemo(() => {
    const hiddenStauses = [
      TEDEKBokforderSTATUSEnum.VERIFIKATION_SKAPAD,
      TEDEKBokforderSTATUSEnum.ATTESTERAD,
      TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
    ];
    return hiddenStauses.includes(formContext?.data?.STATUS);
  }, [formContext?.data?.STATUS]);

  if (attestanter && attestanter !== prevAttestanter) {
    if (!attestanter.includes(',')) {
      const newValdAttestant =
        bokforderContext.allBokforderUsersWithKodbeh.find(
          bokforderUser => bokforderUser.namn === attestanter
        );

      setPrevAttestanter(attestanter);
      setValdAttestant(newValdAttestant ?? null);
    } else {
      setPrevAttestanter(attestanter);
      setValdAttestant(null);
    }
  }

  const showAttestantAutoComplete = bokforderContext.isEditMode;

  const paramValuesFromData = parseParamsValues(formContext?.data?.PARAMVALUES);

  const hasBokfbelopp =
    paramValuesFromData?.BOKF_BELOPP != null &&
    paramValuesFromData?.BOKF_BELOPP != '';

  return (
    <EDBox
      sx={{
        width: theme => `calc(100% - ${theme.spacing(2)})`,
        display: 'flex',
        flexFlow: 'row wrap',
        columnGap: 1,
        paddingX: theme => theme.spacing(1),
      }}
      testID="bokforderKonteringKort"
    >
      <EDBox component="span" sx={{ width: '100%' }}>
        <EDDivider margin={1} />
      </EDBox>
      <EDInputGroup>
        <EDBox
          sx={{
            display: 'flex',
            flex: '1 1 100%',
            flexFlow: 'row wrap',
            columnGap: 1,
          }}
        >
          {!hideTemplateInputFields && (
            <>
              {params &&
                params.map((param, index) => {
                  const isDateType = paramIsDateType(param.PARAM_DATATYP);
                  const isCustomField =
                    (templateAttributes as Record<string, IClientAttributeModel>)[param.PARAM_NAME] === undefined;
                  const shouldTargetThis = index === 0;
                  const attribute: IClientAttributeModel = (templateAttributes as Record<string, IClientAttributeModel>)[
                    param.PARAM_NAME
                  ] ?? {
                    AttributeName: param.PARAM_NAME, // AttributeName
                    DisplayLabel: param.PARAM_NAME,
                    Hint: param.PARAM_NAME,
                    DataType: param.PARAM_DATATYP,
                    EnumValues: new Map(),
                    Attribute: 'Normal', // Combination of Attribute and Exportable: Normal = Attribute and Exportable, Internal = Attribute and not Exportable, None = not Attribute
                    Condition: 'Normal', // Combination of Condition and InternalCondition: Normal = Condition and not InternalCondition, Internal = Condition and InternalCondition, None = not Condition
                    Formatter: '', // Formatter
                    FormatterParseParams: '', // FormatterParseParams
                    FormatterDisplayParams: '', // FormatterParseParams
                    FormatterValidateParams: '', // FormatterValidateParams
                    GUIEditable: true, // Can this field be modified by the end user?
                    InterfaceType:
                      param.PARAM_DATATYP ===
                      TEDEKBokforingsmallParamPARAM_DATATYPEnum.TEXT
                        ? 'ifString'
                        : 'ifNumeric', // InterfaceType (Normal Condition only)
                    CondOperators: [
                      TEDSelectionOperator.equals,
                      TEDSelectionOperator.notequals,
                      TEDSelectionOperator.interval,
                      TEDSelectionOperator.notinterval,
                      TEDSelectionOperator.notless,
                      TEDSelectionOperator.greater,
                      TEDSelectionOperator.notgreater,
                      TEDSelectionOperator.less,
                      TEDSelectionOperator.hasvalue,
                      TEDSelectionOperator.isnull,
                    ], // CondOperators (Normal Condition only)
                    DefaultCondOperator: 'interval',
                    ValidInComps: [], // Valid In Classes
                    AttributeGroup: '', // AttributeGroup
                    AttributeParams: {},
                    Properties: new Map(),
                  };

                  if (param.PARAM_NAME === 'MALL_LIKVIDSATT') {
                    return (
                      <EDBox
                        component="span"
                        key={param.PARAM_NAME}
                        sx={theme => ({
                          flex: '0 0 100%',
                          [theme.breakpoints.up('md')]: {
                            flex: '0 0 calc(100%/7)',
                            minWidth: theme.spacing(25),
                          },
                        })}
                      >
                        {bokforderContext.isEditMode ? (
                          <EDAutoCompleteEdit<TEDEKBokforderRadLIKVIDSATTEnum>
                            disabled={
                              !formContext?.isNew || changesAreMadeInGrid
                            }
                            disableOpenOnFocus
                            fullWidth
                            inputRef={
                              shouldTargetThis
                                ? isMediumOrLarger
                                  ? bokforderContext.focusRef
                                  : undefined
                                : undefined
                            }
                            getOptionLabel={option => {
                              switch (option) {
                                case TEDEKBokforderRadLIKVIDSATTEnum.BANK_KONTANT:
                                  return 'Bank kontant';
                                case TEDEKBokforderRadLIKVIDSATTEnum.KASSA_KONTANT:
                                  return 'Kassa kontant';
                                case TEDEKBokforderRadLIKVIDSATTEnum.KORT:
                                  return 'Kort';
                                case TEDEKBokforderRadLIKVIDSATTEnum.SKAP_KONTANT:
                                  return 'Skåp kontant';
                                case TEDEKBokforderRadLIKVIDSATTEnum.SWISH:
                                  return 'Swish';

                                default:
                                  return capitalize(
                                    `${option}`.toLocaleLowerCase('sv-SE'),
                                    0,
                                    1
                                  );
                              }
                            }}
                            hideClear
                            label={param.PARAM_BENAMNING ?? ''}
                            onChange={(option: any) => {
                              if (!Array.isArray(option) && option) {
                                if (option === 'ALLA') {
                                  onChange(param, false, true)('');

                                  // Values exsisting in grid
                                  const addedRowsIDs: string[] =
                                    formContext?.formState?.values?._Associations?.Rader_Add_Values?.filter(
                                      (row: any) => !isRowEmpty(row)
                                    ).map((addedRow: any) => addedRow.id);

                                  // Template rows created from makeTemplateRows in onTemplateChange
                                  const templateRowsIDs =
                                    bokforderContext.templateRows.map(
                                      row => row.id
                                    );

                                  // Row ids that arent in the grid already
                                  const rowIDsDifference = templateRowsIDs
                                    .filter(x => !addedRowsIDs?.includes(x))
                                    .concat(
                                      addedRowsIDs?.filter(
                                        x => !templateRowsIDs?.includes(x)
                                      )
                                    );

                                  // New rows from template rows based on the differnce to already exsisting rows so we dont overrite existing rows
                                  const newRows =
                                    bokforderContext.templateRows.filter(row =>
                                      rowIDsDifference.includes(row.id)
                                    );

                                  bokforderContext.createTemplateRows(newRows);
                                } else {
                                  onChange(param, false, true)(option);

                                  // Template rows that matches the selected method of liquidation
                                  const newRows =
                                    bokforderContext.templateRows.filter(
                                      row =>
                                        !(
                                          row.methodOfLiquidation?.MALL_TYP !=
                                            option &&
                                          row.methodOfLiquidation?.MALL_TYP !=
                                            undefined
                                        )
                                    );

                                  // Row ids that should get deleted from template rows in Bokfordercontext
                                  const deleteTemplateRowsIDs =
                                    bokforderContext.templateRows
                                      .filter(
                                        row =>
                                          row.methodOfLiquidation?.MALL_TYP !=
                                            option &&
                                          row.methodOfLiquidation?.MALL_TYP !=
                                            undefined
                                      )
                                      .map(row => row.id);

                                  // Values exsisting in grid
                                  const addValuesRows: any[] =
                                    formContext?.formState?.values
                                      ?._Associations?.Rader_Add_Values;

                                  // Delete empty rows aswell
                                  const deleteRowsIDs = addValuesRows
                                    .filter(addedRows => isRowEmpty(addedRows))
                                    .map(row => row.id)
                                    .concat(deleteTemplateRowsIDs);

                                  // Transforms rows so we easily can sort on them based on index in added rows
                                  const deleteRows: {
                                    id: string;
                                    index: number;
                                  }[] = mergeSort(
                                    addValuesRows
                                      .filter(addRow =>
                                        deleteRowsIDs.includes(addRow.id)
                                      )
                                      .map(row => ({
                                        id: row.id,
                                        index: addValuesRows.findIndex(
                                          (rad: any) => rad.id === row.id
                                        ),
                                      })),
                                    (a, b) => {
                                      if (!a?.index && !b?.index) {
                                        return 0;
                                      }

                                      if (a.index > b.index) {
                                        return -1;
                                      }

                                      if (b.index > a.index) {
                                        return 1;
                                      }
                                      return 0;
                                    }
                                  );
                                  // Deleted rows based on index in desc order so we don´t change indexes on the values in Rader_Add_Values
                                  deleteRows.forEach(deleteRow => {
                                    bokforderContext.deleteBokforderRad(
                                      deleteRow.id,
                                      true
                                    );
                                  });
                                  // Create any missing rows for this method of liquidation
                                  bokforderContext.createTemplateRows(newRows);
                                }
                              }
                            }}
                            options={
                              bokforderContext.methodOfLiquidation as TEDEKBokforderRadLIKVIDSATTEnum[]
                            }
                            value={
                              (formContext?.formState.currentValues?.[
                                param.PARAM_NAME
                              ] === ''
                                ? 'ALLA'
                                : formContext?.formState.currentValues?.[
                                    param.PARAM_NAME
                                  ]) ?? 'ALLA'
                            }
                            testId="MALL_LIKVIDSATT-EDAutoCompleteEdit"
                          />
                        ) : (
                          <EDTypography
                            sx={{
                              padding: '6px 8px 0px',
                            }}
                          >
                            <EDTypography bold component="span">
                              Likvidsätt:
                            </EDTypography>{' '}
                            {formContext?.data?.MALL_LIKVIDSATT ?? 'Alla'}
                          </EDTypography>
                        )}
                      </EDBox>
                    );
                  }

                  return (
                    <EDBox
                      component="span"
                      key={param.PARAM_NAME}
                      sx={theme => ({
                        flex: '0 0 100%',
                        [theme.breakpoints.up('md')]: {
                          flex: '0 0 calc(100%/7)',
                          minWidth: theme.spacing(25),
                        },
                      })}
                    >
                      <EditableAttribute
                        inputRef={
                          shouldTargetThis
                            ? isMediumOrLarger
                              ? bokforderContext.focusRef
                              : undefined
                            : undefined
                        }
                        key={param.PARAM_NAME}
                        attribute={attribute}
                        collection={bokforderModel}
                        debounceDelayTime={
                          isDateType ? 500 : DEBOUNCE_DELAY_TIME
                        }
                        editMode={bokforderContext.isEditMode}
                        label={param.PARAM_BENAMNING}
                        onChange={onChange(param, isCustomField, false)}
                        size="ownblock"
                        value={bokforderContext.paramValues?.[param.PARAM_NAME]}
                        onBlur={() =>
                          formContext?.formStateDispatch({
                            type: 'TOUCH',
                            path: [param.PARAM_NAME],
                          })
                        }
                        warningMessage={
                          formContext?.formState.validation.find(
                            validation =>
                              validation.fieldName === param.PARAM_NAME &&
                              validation.type === 'Warning' &&
                              formContext.formState.touched?.[param.PARAM_NAME]
                          )?.message
                        }
                      />
                    </EDBox>
                  );
                })}
            </>
          )}
        </EDBox>
        <EDBox
          component="span"
          sx={theme => ({
            flex: '0 0 100%',
            height: bokforderContext.isEditMode ? '63px' : 'auto',
            [theme.breakpoints.up('md')]: {
              flex: '0 0 calc(100%/7)',
              minWidth: theme.spacing(25),
            },
          })}
        >
          <Attribut
            inputRef={bokforderContext.focusRef}
            name="BOKF_DATUM"
            disableFutureDates
            autoFocus={
              isMediumOrLarger
                ? formContext?.isNew
                  ? !(
                      bokforderContext.bokforderVariant ===
                        TEDEKBokforderVariantEnum.MALL ||
                      bokforderContext.bokforderVariant ===
                        DefaultBfoVariantEnum.VERIFIKATION ||
                      bokforderContext.bokforderVariant ===
                        TEDEKBokforderVariantEnum.PERIODISERA
                    )
                    ? true
                    : false
                  : false
                : false
            }
            customDisplayLabel={'Beställt Bokf.datum'}
            debounceDelayTime={0}
            isRequired
            disableEditMode={!bokforderContext.isEditMode}
            size="ownblock"
          />
        </EDBox>
        {!hideTemplateInputFields &&
          bokforderContext.currentTemplate &&
          (hasBokfbelopp ||
            !bokforderContext.currentTemplate.EXKLUDERA_BELOPP) && (
            <EDBox
              component="span"
              sx={theme => ({
                flex: '0 0 100%',
                height: bokforderContext.isEditMode ? '63px' : 'auto',
                [theme.breakpoints.up('md')]: {
                  flex: '0 0 calc(100%/7)',
                  minWidth: theme.spacing(25),
                },
              })}
            >
              <EditableAttribute
                attribute={beloppAttribute}
                collection={bokforderModel}
                editMode={bokforderContext.isEditMode}
                onChange={onBeloppChange}
                required={true}
                size="ownblock"
                value={bokforderContext.paramValues?.BOKF_BELOPP}
                onBlur={() =>
                  formContext?.formStateDispatch({
                    type: 'TOUCH',
                    path: ['BOKF_BELOPP'],
                  })
                }
                errorMessage={
                  formContext?.formState.validation.find(
                    validation =>
                      validation.fieldName === 'BELOPP' &&
                      validation.type === 'Error' &&
                      formContext.formState.touched?.BOKF_BELOPP
                  )?.message
                }
              />
            </EDBox>
          )}
        <EDBox
          sx={{
            display: 'flex',
            width: '100%',
            marginY: theme => theme.spacing(1),
            columnGap: 1,
          }}
        >
          <EDBox
            sx={{
              flex: showAttestantAutoComplete ? '5 0 68%' : '0 0 100%',
            }}
          >
            <Attribut
              customDisplayLabel="Rubrik"
              disableEditMode={!bokforderContext.isEditMode}
              name="TEXT"
              size="large"
              isRequired
            />
          </EDBox>
          {showAttestantAutoComplete && (
            <EDBox
              sx={{
                flex: '1 0 30%',
              }}
            >
              <AttestantPicker
                isDisabled={isAttestantPickerDisabled}
                kontodelDefFromKodplan={kontodelDefFromKodplan}
                setValdAttestant={setValdAttestant}
                type={TEDEKBokforderTYPEnum.BOKFORINGSORDER}
                valdAttestant={valdAttestant}
              />
            </EDBox>
          )}
        </EDBox>
      </EDInputGroup>
    </EDBox>
  );
};

export const BokforderKontering: IObjektVy = {
  type: 'ObjektVy',
  id: 'BokforderKontering',
  info: {},
  def: () => BokforderKonteringComp,
};
